// Quality color tiering for metric values. **Must stay in sync with
// internal/ranking/constants.go** — if a threshold moves there, update
// it here too. Centralized here so every table cell and chart legend
// item consumes one source of truth.
//
// Returns a Tailwind class string rather than a hex value so rows pick
// up the project's theme (light/dark) without threading palette state.

import type { Trend } from "./api";

export type Tier = "good" | "marginal" | "bad" | "unknown";

export const RET_GOOD = 0.15;
export const RET_OK = 0.08;
export const HIT_GOOD = 0.6;
export const HIT_OK = 0.4;
export const ATR_GOOD = 0.03;
export const ATR_OK = 0.05;
export const POS_GOOD = 0.25;
export const POS_OK = 0.5;
export const RS_GOOD = 0.05;
export const RS_OK = -0.05;
export const RR_GOOD = 3.0;
export const RR_OK = 2.0;
export const RISK_GOOD = 0.05;
export const RISK_OK = 0.1;
export const DROP_GOOD = 0.05;
export const DROP_OK = 0.1;

export function qualityTier(
  value: number | null,
  good: number,
  ok: number,
  higherIsBetter = true
): Tier {
  if (value == null || !Number.isFinite(value)) return "unknown";
  if (higherIsBetter) {
    if (value >= good) return "good";
    if (value >= ok) return "marginal";
    return "bad";
  }
  if (value <= good) return "good";
  if (value <= ok) return "marginal";
  return "bad";
}

// Tailwind classes for the four tiers. Using emerald/blue/red so the
// palette survives dark mode (shadcn's default CSS variables already
// handle the background contrast).
export function tierClass(tier: Tier): string {
  switch (tier) {
    case "good":
      return "text-emerald-600 dark:text-emerald-400";
    case "marginal":
      return "text-sky-600 dark:text-sky-400";
    case "bad":
      return "text-rose-600 dark:text-rose-400";
    case "unknown":
      return "text-muted-foreground";
  }
}

// Convenience wrappers that encode "higher is better" once.
export function retClass(v: number | null): string {
  return tierClass(qualityTier(v, RET_GOOD, RET_OK, true));
}
export function hitClass(v: number | null): string {
  return tierClass(qualityTier(v, HIT_GOOD, HIT_OK, true));
}
export function atrClass(v: number | null): string {
  return tierClass(qualityTier(v, ATR_GOOD, ATR_OK, false));
}
export function posClass(v: number | null): string {
  return tierClass(qualityTier(v, POS_GOOD, POS_OK, false));
}
export function rsClass(v: number | null): string {
  return tierClass(qualityTier(v, RS_GOOD, RS_OK, true));
}
export function rrClass(v: number | null): string {
  return tierClass(qualityTier(v, RR_GOOD, RR_OK, true));
}
export function riskClass(v: number | null): string {
  return tierClass(qualityTier(v, RISK_GOOD, RISK_OK, false));
}
export function dropClass(v: number | null): string {
  return tierClass(qualityTier(v, DROP_GOOD, DROP_OK, false));
}

// Ticker verdict tier — mirrors internal/cli/color.go TickerStyleFor
// including the hit-rate veto. Phase 4a bands for the ±5 score range:
// ≥4 bold green, 1..3 green, 0 marginal, ≤−1 bad.
export function tickerTier(score: number, hit: number | null): Tier {
  let tier: Tier;
  if (score >= 4) tier = "good"; // bold green
  else if (score >= 1) tier = "good";
  else if (score <= -1) tier = "bad";
  else tier = "marginal";

  // Hit-rate veto: concrete hit == 0 demotes green → marginal.
  if (hit !== null && hit === 0 && tier === "good") {
    return "marginal";
  }
  return tier;
}

// Whether the ticker should be bold (score >= 4, hit not vetoed).
export function tickerBold(score: number, hit: number | null): boolean {
  return tickerTier(score, hit) === "good" && score >= 4;
}

export const TREND_GLYPHS: Record<Trend, string> = {
  up: "↑",
  down: "↓",
  neutral: "–",
  unknown: "?",
};

export function trendClass(t: Trend): string {
  switch (t) {
    case "up":
      return "text-emerald-600 dark:text-emerald-400";
    case "down":
      return "text-rose-600 dark:text-rose-400";
    default:
      return "text-muted-foreground";
  }
}
